import { nanoid } from 'nanoid'
import { MediaImageRepositoryProvider } from '@/scenes/engine/objects/media-repository/MediaImageRepositoryProvider'
import { MediaImageRepositoryProcessingOffscreen } from '@/shared/MediaImageRepositoryProcessingOffscreen'
import {
  INDEXED_IMAGE_ACTIONS,
  INDEXED_IMAGE_TRANSFER_QUERY_PARAM,
  IndexedImageAction,
  OPEN_IN_EDITOR_SOURCE_PROJECT_QUERY_PARAM,
  saveIndexedImage,
} from '@/utils/indexedImage'

export type OpenImageSource = Blob | string | URL

/**
 * Opens a blank tab synchronously so it stays inside the originating user
 * gesture. Safari blocks `window.open` that runs after an `await`, so callers
 * that must `await` before reaching `openImageSourceInEditor` should open the
 * tab here first and pass the handle in.
 */
export function openBlankEditorTab(): Window | null {
  return window.open('', '_blank')
}

export async function openImageSourceInEditor(
  source: OpenImageSource,
  action: IndexedImageAction = INDEXED_IMAGE_ACTIONS.OPEN_EDITOR_AI_FASHION_MODELS,
  pendingEditorTab?: Window | null,
): Promise<void> {
  // The popup MUST be opened synchronously within the user gesture, otherwise
  // Safari blocks it ("popup blocked") and the resulting tab shows an infinite
  // loader because navigation never reaches the editor. When the caller has not
  // already opened a tab (no async work before this call), open it here while we
  // are still synchronously inside the gesture; otherwise reuse the caller's tab.
  const editorTab = pendingEditorTab !== undefined ? pendingEditorTab : openBlankEditorTab()

  try {
    const imageProcessingOffscreen = new MediaImageRepositoryProcessingOffscreen()
    const blob = await imageProcessingOffscreen.toBlob(source)
    const transferId = nanoid()
    await saveIndexedImage(blob, action, transferId)
    const openUrl = new URL(window.location.origin)
    const sourceProjectId = MediaImageRepositoryProvider.getCurrentProjectId()
    openUrl.searchParams.set(INDEXED_IMAGE_TRANSFER_QUERY_PARAM, transferId)
    if (sourceProjectId) {
      openUrl.searchParams.set(OPEN_IN_EDITOR_SOURCE_PROJECT_QUERY_PARAM, sourceProjectId)
    }

    if (editorTab) {
      // Navigate the already-open (gesture-safe) tab to the editor.
      editorTab.location.href = openUrl.toString()
    } else {
      // Tab was blocked despite the synchronous open; try once more directly.
      window.open(openUrl.toString(), '_blank')
    }
  } catch (error) {
    editorTab?.close()
    throw error
  }
}
